import {ExternalIntegrationPurpose} from 'enums';
import {SessionActivityName} from 'interfaces';

export enum ComplianceDecision {
    Accepted = 'accepted',
    Ignored = 'ignored'
}

// Absent on legacy records — an absent author means the message was written by the agent.
export enum ComplianceMessageAuthor {
    Agent = 'agent',
    Bot = 'bot'
}

export type ComplianceCheckResult = {
    flagged: boolean;
    verdictId?: string;
    rule?: string;
    reason?: string;
    suggestedRewording?: string;
}

export type ComplianceDecisionMeta = {
    verdictId: string;
    decision: ComplianceDecision;
}

export type ComplianceAuditFilters = {
    startDate?: string;
    endDate?: string;
    eid?: number;
    groupId?: string;
    subgroupId?: string;
    sessionId?: string;
    activityName?: SessionActivityName;
    purpose?: ExternalIntegrationPurpose;
}

export type ComplianceStatisticTotals = {
    checks: number;
    passed: number;
    flagged: number;
    errors: number;
    accepted: number;
    ignored: number;
    dismissed: number;
}

export type ComplianceDailyStatistic = {
    date: string;
    checks: number;
    passed: number;
    flagged: number;
    ignored: number;
    errors: number;
}

// Non-blocking hint pushed to the agent's user socket for flagged audio-transcription windows.
export type ComplianceHint = {
    sessionId: string;
    rule?: string;
    reason?: string;
    guidance?: string;
    // Comes from the activity settings; null means the notification stays until the agent closes it.
    durationSeconds?: number | null;
}

// Daily-chart series toggled by the KPI cards; values match ComplianceDailyStatistic fields.
export enum ComplianceChartMetric {
    Checks = 'checks',
    Flagged = 'flagged',
    Ignored = 'ignored',
    Errors = 'errors'
}

export type ComplianceRuleStatistic = {
    rule: string;
    flagged: number;
    accepted: number;
    ignored: number;
    dismissed: number;
}

export type ComplianceAgentStatistic = {
    agentEid: number;
    agentEmail: string;
    checks: number;
    flagged: number;
    accepted: number;
    ignored: number;
    dismissed: number;
    errors: number;
}

export type ComplianceStatistic = {
    totals: ComplianceStatisticTotals;
    daily: ComplianceDailyStatistic[];
    rules: ComplianceRuleStatistic[];
    agents: ComplianceAgentStatistic[];
}

export type ComplianceAuditMessage = {
    verdictId: string;
    createdAt: string;
    agentEid?: number;
    agentEmail?: string;
    author?: ComplianceMessageAuthor;
    sessionId: string;
    activityName?: SessionActivityName;
    rule?: string;
    reason?: string;
    originalMessage: string;
    suggestedRewording?: string;
    finalMessage?: string;
    correctedMessage?: string;
    correctedAt?: string;
    decision?: ComplianceDecision;
    decidedAt?: string;
}

export type ComplianceAuditMessages = {
    items: ComplianceAuditMessage[];
    total: number;
}
